import { useState } from "react";
import { AlertTriangle, Check, Copy } from "lucide-react";

import type { ProviderCredentialResponse } from "@/api";
import { Alert } from "@/components/ui/alert";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogBody,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { useTranslation } from "@/i18n";
import { formatDateID } from "@/lib/format";
import { formatProviderLabel } from "@/lib/provider-helpers";

interface Props {
  /** Credential + plaintext bundle from a successful create / rotate.
   *  ``plaintext`` is the value pulled from the show-once envelope
   *  (NOT the credential's stored ``api_key_plaintext`` — that field
   *  can be empty on metadata-only updates). */
  data: { credential: ProviderCredentialResponse; plaintext: string } | null;
  /** Headline copy varies between "dibuat" (create) and "dirotasi"
   *  (rotate). Defaults to "dibuat". */
  variant?: "created" | "rotated";
  onClose: () => void;
}

/**
 * Modal-locked show-once dialog for BYOK credentials. Mirrors the
 * FE-2 API-key + FE-4 signup-key pattern: backdrop-click + Escape
 * are suppressed so the user can't lose the plaintext key by
 * accident, and an acknowledge checkbox gates the close button.
 */
export function ProviderShowOnceDialog({
  data,
  variant = "created",
  onClose,
}: Props) {
  const { t } = useTranslation();
  const [copied, setCopied] = useState(false);
  const [acknowledged, setAcknowledged] = useState(false);

  if (!data) return null;

  const { credential, plaintext } = data;
  const headline =
    variant === "rotated"
      ? t("providers.showOnceDialog.headlineRotated")
      : t("providers.showOnceDialog.headlineCreated");

  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(plaintext);
      setCopied(true);
      window.setTimeout(() => setCopied(false), 2000);
    } catch {
      // Clipboard may be blocked (Safari private / sandbox);
      // user can still select + copy manually.
    }
  };

  const handleClose = () => {
    setCopied(false);
    setAcknowledged(false);
    onClose();
  };

  return (
    <Dialog open onClose={handleClose} size="max-w-lg" modal>
      <DialogHeader>
        <DialogTitle>
          <span className="inline-flex items-center gap-2">
            <Check size={18} className="text-success-600" />
            {headline}
          </span>
        </DialogTitle>
        <DialogDescription>
          {t("providers.showOnceDialog.description")}
        </DialogDescription>
      </DialogHeader>

      <DialogBody>
        <div className="rounded-md border border-ocean-100 bg-ocean-50/40 px-4 py-3">
          <div className="label-eyebrow mb-2">
            {formatProviderLabel(credential.provider_type)} ·{" "}
            {credential.credential_name}
          </div>
          <div className="flex items-stretch gap-2">
            <code className="flex-1 break-all rounded border border-ocean-100 bg-white px-3 py-2 font-mono text-sm text-ocean-900">
              {plaintext}
            </code>
            <Button
              type="button"
              variant="outline"
              size="sm"
              onClick={handleCopy}
              className="shrink-0"
              aria-label={
                copied
                  ? t("providers.showOnceDialog.copiedAria")
                  : t("providers.showOnceDialog.copyAria")
              }
            >
              {copied ? (
                <>
                  <Check size={14} className="text-success-600" />
                  {t("providers.showOnceDialog.copied")}
                </>
              ) : (
                <>
                  <Copy size={14} />
                  {t("providers.showOnceDialog.copy")}
                </>
              )}
            </Button>
          </div>
        </div>

        <Alert variant="warning">
          <div className="flex items-start gap-2 text-xs">
            <AlertTriangle size={14} className="mt-0.5 shrink-0" />
            <div>
              <strong>{t("providers.showOnceDialog.warningTitle")}</strong>{" "}
              {t("providers.showOnceDialog.warningBody")}
            </div>
          </div>
        </Alert>

        <div className="grid grid-cols-1 gap-1 rounded-md bg-neutral-50 px-3 py-2 text-xs text-neutral-600 tablet:grid-cols-2">
          <div>
            <span className="font-medium text-neutral-800">
              {t("providers.showOnceDialog.last4Label")}
            </span>{" "}
            <code className="text-ocean-700">{credential.api_key_last_4}</code>
          </div>
          <div>
            <span className="font-medium text-neutral-800">
              {t("providers.showOnceDialog.createdLabel")}
            </span>{" "}
            {formatDateID(credential.created_at)}
          </div>
        </div>

        <label className="flex items-start gap-2 text-xs text-neutral-700">
          <input
            type="checkbox"
            checked={acknowledged}
            onChange={(e) => setAcknowledged(e.target.checked)}
            className="mt-0.5 h-4 w-4 rounded border-ocean-200 text-ocean-600 focus:ring-ocean-400"
          />
          <span>{t("providers.showOnceDialog.acknowledge")}</span>
        </label>
      </DialogBody>

      <DialogFooter>
        <Button type="button" onClick={handleClose} disabled={!acknowledged}>
          {t("providers.showOnceDialog.close")}
        </Button>
      </DialogFooter>
    </Dialog>
  );
}
